import React, { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";

// Languages we expose. Google Translate handles the actual translation.
const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "bn", label: "বাংলা" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "zh-CN", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "ru", label: "Русский" },
];

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  const host = window.location.hostname;
  document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=.${host}`;
};

const LanguageSwitcher = ({ dark = false }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const ref = useRef(null);

  useEffect(() => {
    // Inject Google Translate script once
    if (document.getElementById("google-translate-script")) return;
    window.googleTranslateElementInit = function () {
      // eslint-disable-next-line no-new, no-undef
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: LANGS.map((l) => l.code).join(","),
          autoDisplay: false,
        },
        "google_translate_element"
      );
    };
    const s = document.createElement("script");
    s.id = "google-translate-script";
    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (code) => {
    setCurrent(code);
    setOpen(false);

    if (code === "en") {
      // Clear translation cookie & reload to English
      setCookie("googtrans", "", -1);
      document.cookie = "googtrans=; Max-Age=0; path=/";
      window.location.reload();
      return;
    }

    const val = `/en/${code}`;
    setCookie("googtrans", val);
    window.location.reload();
  };

  const label = LANGS.find((l) => l.code === current)?.label || "English";

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      {/* Hidden Google widget */}
      <div id="google_translate_element" style={{ display: "none" }} />

      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="language-toggle"
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-colors ${
          dark
            ? "bg-white/10 text-white hover:bg-white/20 border border-white/20"
            : "bg-orange-50 text-[#1a0a05] hover:bg-orange-100 border border-orange-200"
        }`}
        aria-label="Select language"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="language-menu"
          className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-orange-100 py-2 z-[60] max-h-80 overflow-y-auto"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              data-testid={`lang-${l.code}`}
              className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors hover:bg-orange-50 ${
                current === l.code ? "text-orange-600" : "text-[#1a0a05]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
